import type { ReactNode } from 'react';

import type { Tone } from './Badge';

/** Tinted cards for the dashboard tiles. `undefined` is the plain surface card. */
const TONE_SURFACES: Partial<Record<Tone, string>> = {
  danger: 'bg-danger-bg border-danger-border',
  warning: 'bg-warning-bg border-warning-border',
  success: 'bg-success-bg border-success-border',
  info: 'bg-info-bg border-info-border',
};

interface CardProps {
  children: ReactNode;
  tone?: Tone;
  className?: string;
}

export function Card({ children, tone, className = '' }: CardProps) {
  const surface = (tone && TONE_SURFACES[tone]) ?? 'bg-surface border-line';
  return (
    <div className={`rounded-xl border p-5 shadow-card ${surface} ${className}`}>{children}</div>
  );
}

interface CardHeaderProps {
  title: string;
  subtitle?: string;
  action?: ReactNode;
}

export function CardHeader({ title, subtitle, action }: CardHeaderProps) {
  return (
    <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <h2 className="text-base font-semibold text-body">{title}</h2>
        {subtitle ? <p className="mt-0.5 text-xs text-subtle">{subtitle}</p> : null}
      </div>
      {action ? <div className="flex shrink-0 items-center gap-2">{action}</div> : null}
    </div>
  );
}

interface StatCardProps {
  label: string;
  value: string | number;
  hint?: string;
  tone?: Tone;
  /** Colours the number when the tile is tinted. */
  valueTone?: 'body' | 'danger' | 'warning' | 'success';
  onClick?: () => void;
}

const VALUE_TONES: Record<NonNullable<StatCardProps['valueTone']>, string> = {
  body: 'text-body',
  danger: 'text-danger-fg',
  warning: 'text-warning-fg',
  success: 'text-success-fg',
};

export function StatCard({
  label,
  value,
  hint,
  tone,
  valueTone = 'body',
  onClick,
}: StatCardProps) {
  const interactive = onClick
    ? 'cursor-pointer transition-shadow duration-150 hover:shadow-md'
    : '';
  return (
    <Card tone={tone} className={`flex flex-col gap-2 ${interactive}`}>
      {onClick ? (
        // A tile that filters a list is a button, not a div with a click handler — it has to
        // be reachable and activatable from the keyboard.
        <button
          type="button"
          onClick={onClick}
          className="flex w-full flex-col gap-2 text-left"
        >
          <StatBody label={label} value={value} hint={hint} valueTone={valueTone} />
        </button>
      ) : (
        <StatBody label={label} value={value} hint={hint} valueTone={valueTone} />
      )}
    </Card>
  );
}

function StatBody({
  label,
  value,
  hint,
  valueTone,
}: Pick<StatCardProps, 'label' | 'value' | 'hint'> & {
  valueTone: NonNullable<StatCardProps['valueTone']>;
}) {
  return (
    <>
      <span className="text-sm font-medium text-muted">{label}</span>
      <span className={`text-3xl font-bold tabular-nums ${VALUE_TONES[valueTone]}`}>{value}</span>
      {hint ? <span className="text-xs text-subtle">{hint}</span> : null}
    </>
  );
}
